import { useQuery } from '@tanstack/react-query';
import { ProductDetail, ProductResponse, ProductFilters, ProductBase } from '@/types/product';
import { Response, PaginatedResponse, PaginationResponse } from '@/types/common';
import { PaginationOptions } from '@/utils/serviceUtils';
import { CategoryFiltersResponse } from '@/services/prestashop/productService';

// Función para obtener todos los productos
const fetchProducts = async (
  lang: string = 'es',
  filters?: ProductFilters
): Promise<ProductDetail[]> => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
  }

  const url = `/api/${lang}/products${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }

  const data: Response<ProductDetail[]> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al obtener productos');
  }

  return data.data || [];
};

// Función para obtener un producto específico
const fetchProduct = async (id: number, lang: string = 'es'): Promise<ProductDetail> => {
  const response = await fetch(`/api/${lang}/products/${id}`);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }

  const data: Response<ProductDetail> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al obtener producto');
  }

  return data.data;
};

// Función para obtener un producto por slug
const fetchProductBySlug = async (slug: string, lang: string = 'es'): Promise<ProductDetail> => {
  const response = await fetch(`/api/${lang}/products/slug/${slug}`);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }

  const data: Response<ProductDetail> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al obtener producto');
  }

  return data.data;
};

const fetchProductsByCategory = async (
  categoryIds: number[],
  lang: string = 'es',
  filters?: ProductFilters,
  sort?: string,
  pagination?: PaginationOptions
): Promise<CategoryFiltersResponse> => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
  }

  // Agregar los IDs de categorías como parámetros
  categoryIds.forEach((id) => params.append('categoryIds', id.toString()));
  if (sort) {
    params.append('sort', sort);
  }

  // Agregar parámetros de paginación
  if (pagination) {
    if (pagination.page) {
      params.append('page', pagination.page.toString());
    }
    if (pagination.limit) {
      params.append('limit', pagination.limit.toString());
    }
  }

  const url = `/api/${lang}/products/category${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }
  const data: Response<CategoryFiltersResponse> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al obtener productos de categoría');
  }

  return data.data || {};
};

const fetchProductsByManufacturer = async (
  manufacturerId: number,
  lang: string = 'es',
  filters?: ProductFilters
): Promise<ProductDetail[]> => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
  }

  const url = `/api/${lang}/products/manufacturer/${manufacturerId}${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }

  const data: Response<ProductDetail[]> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al obtener productos de fabricante');
  }

  return data.data || [];
};

const fetchProductsOnSale = async (
  lang: string = 'es',
  filters?: ProductFilters
): Promise<ProductDetail[]> => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
  }

  const url = `/api/${lang}/products/on-sale${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }

  const data: Response<ProductDetail[]> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al obtener productos en oferta');
  }

  return data.data || [];
};

const fetchNewProducts = async (
  lang: string = 'es',
  filters?: ProductFilters
): Promise<ProductDetail[]> => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
  }

  const url = `/api/${lang}/products/new${params.toString() ? `?${params.toString()}` : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }

  const data: ProductResponse = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al obtener productos nuevos');
  }

  return data.data.products || [];
};

// Función para obtener productos por IDs específicos
const fetchProductsByIds = async (
  productIds: string[],
  lang: string = 'es'
): Promise<ProductBase[]> => {
  if (!productIds || productIds.length === 0) {
    return [];
  }

  const params = new URLSearchParams();
  productIds.forEach((id) => params.append('ids', id));

  const url = `/api/${lang}/products/by-ids?${params.toString()}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }

  const data: Response<ProductBase[]> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al obtener productos por IDs');
  }

  return data.data || [];
};

const fetchProductsBySearch = async (
  search: string,
  lang: string = 'es',
  filters?: ProductFilters
): Promise<ProductDetail[]> => {
  const params = new URLSearchParams();
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
  }

  const url = `/api/${lang}/products/search?${params.toString()}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }

  const data: ProductResponse = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al buscar productos');
  }

  return data.data.products || [];
};

const fetchProductsBySearchWithPagination = async (
  search: string,
  lang: string = 'es',
  options: {
    page?: number;
    perPage?: number;
    categoryId?: number;
  } = {}
): Promise<{ data: ProductBase[]; pagination: PaginationResponse }> => {
  const { page = 1, perPage = 10, categoryId } = options;

  const params = new URLSearchParams();
  params.append('q', search);
  params.append('page', page.toString());
  params.append('per_page', perPage.toString());
  if (categoryId) {
    params.append('category_id', categoryId.toString());
  }

  const url = `/api/${lang}/products/search?${params.toString()}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }

  const data: PaginatedResponse<ProductBase[]> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al buscar productos');
  }

  return {
    data: data.data,
    pagination: data.pagination,
  };
};

// Hook principal para obtener todos los productos
export function useProducts(lang: string = 'es', filters?: ProductFilters) {
  return useQuery({
    queryKey: ['products', lang, filters],
    queryFn: () => fetchProducts(lang, filters),
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Hook para obtener un producto específico por ID
export function useProduct(id: number, lang: string = 'es') {
  return useQuery({
    queryKey: ['product', id, lang],
    queryFn: () => fetchProduct(id, lang),
    enabled: !!id,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Hook para obtener un producto por slug
export function useProductBySlug(slug: string, lang: string = 'es') {
  return useQuery({
    queryKey: ['product-slug', slug, lang],
    queryFn: () => fetchProductBySlug(slug, lang),
    enabled: !!slug,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Hook para buscar productos
export function useSearchProducts(
  searchTerm: string,
  lang: string = 'es',
  filters?: ProductFilters
) {
  return useQuery({
    queryKey: ['search-products', searchTerm, lang, filters],
    queryFn: async () => fetchProductsBySearch(searchTerm, lang, filters),
    enabled: !!searchTerm && searchTerm.length >= 2,
    staleTime: 5 * 60 * 1000, // 5 minutos para búsquedas
    gcTime: 15 * 60 * 1000, // 15 minutos
  });
}

// Hook para buscar productos con paginación
export function useSearchProductsWithPagination(
  searchTerm: string,
  lang: string = 'es',
  options: {
    page?: number;
    perPage?: number;
    categoryId?: number;
  } = {}
) {
  return useQuery({
    queryKey: ['search-products-paginated', searchTerm, lang, options],
    queryFn: async () => fetchProductsBySearchWithPagination(searchTerm, lang, options),
    enabled: !!searchTerm && searchTerm.length >= 3,
    staleTime: 5 * 60 * 1000, // 5 minutos para búsquedas
    gcTime: 15 * 60 * 1000, // 15 minutos
  });
}

// Hook para obtener productos por categoría
export function useProductsByCategory(
  categoryIds: number[],
  lang: string = 'es',
  filters?: ProductFilters,
  sort?: string,
  pagination?: PaginationOptions,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['products-category', categoryIds, lang, filters, sort, pagination],
    queryFn: async () => fetchProductsByCategory(categoryIds, lang, filters, sort, pagination),
    enabled: categoryIds?.length > 0 && options?.enabled,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Hook para obtener productos por fabricante
export function useProductsByManufacturer(
  manufacturerId: number,
  lang: string = 'es',
  filters?: ProductFilters
) {
  return useQuery({
    queryKey: ['products-manufacturer', manufacturerId, lang, filters],
    queryFn: async () => fetchProductsByManufacturer(manufacturerId, lang, filters),
    enabled: !!manufacturerId,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Hook para obtener productos en oferta
export function useProductsOnSale(lang: string = 'es', filters?: ProductFilters) {
  return useQuery({
    queryKey: ['products-on-sale', lang, filters],
    queryFn: async () => fetchProductsOnSale(lang, filters),
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Hook para obtener productos nuevos
export function useNewProducts(lang: string = 'es', filters?: ProductFilters) {
  return useQuery({
    queryKey: ['new-products', lang, filters],
    queryFn: async () => fetchNewProducts(lang, filters),
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Hook para obtener productos por IDs específicos
export function useProductsByIds(productIds: string[], lang: string = 'es') {
  return useQuery({
    queryKey: ['products-by-ids', productIds, lang],
    queryFn: async () => fetchProductsByIds(productIds, lang),
    enabled: productIds.length > 0,
    staleTime: 10 * 60 * 1000, // 10 minutos
    gcTime: 30 * 60 * 1000, // 30 minutos
  });
}

// Función utilitaria para obtener productos por IDs (sin cache)
export function getProductsByIds(
  productIds: string[],
  lang: string = 'es'
): Promise<ProductBase[]> {
  return fetchProductsByIds(productIds, lang);
}

// Función para obtener el stock de un producto
export const fetchProductStock = async (
  productId: number,
  lang: string = 'es'
): Promise<number> => {
  const response = await fetch(`/api/${lang}/products/${productId}/stock`);

  if (!response.ok) {
    throw new Error(`Error HTTP: ${response.status}`);
  }

  const data: Response<{ stock: number | Array<{ quantity: number }> }> = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Error al obtener stock del producto');
  }

  const stock = data.data.stock;
  if (typeof stock === 'number' && !Number.isNaN(stock)) {
    return stock;
  }
  if (Array.isArray(stock)) {
    return stock.reduce((sum, stockItem) => sum + stockItem.quantity, 0);
  }
  return 0;
};

// Hook para obtener el stock de un producto
export function useProductStock(productId: number | null, lang: string = 'es') {
  return useQuery({
    queryKey: ['product-stock', productId, lang],
    queryFn: () => fetchProductStock(productId!, lang),
    enabled: !!productId && productId > 0,
    staleTime: 1 * 60 * 1000, // 1 minuto (el stock puede cambiar frecuentemente)
    gcTime: 5 * 60 * 1000, // 5 minutos
  });
}
